import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { Logo } from "assets/index";
import EyeHiddenIcon from "assets/svg/EyeHiddenIcon";
import EyeVisibleIcon from "assets/svg/EyeVisibleIcon";
import { AxiosError } from "axios";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { Field, Form, Formik } from "formik";
import * as motion from "motion/react-client";
import { ChangeEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthRegisterRequest } from "reduxStore/dataTypes";
import { register } from "services/apiService";
import theme from "themes/Theme";
import { registerSchema } from "validations/ValidationsSchema";
import { commonStyle } from "../common.styles";

const SignupPage = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const navigate = useNavigate();

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const validate = (values: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => {
    const result = registerSchema.safeParse(values);
    if (!result.success) {
      return result.error.format();
    }
    return {};
  };

  const handleRegister = async (
    values: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
    },
    resetForm: () => void
  ) => {
    try {
      if (isChecked) {
        setLoading(true);
        const body: AuthRegisterRequest = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
        };

        const response = await register(body);
        if (response && response.status === 200) {
          toast.success(response?.data?.message);
          resetForm();
          navigate("/auth/verify-code", { state: response.data?.data });
        }
      } else {
        toast.warning("Please accept terms and conditions");
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsChecked(checked);
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      transition={{
        type: "spring",
        stiffness: 50,
      }}
      style={{
        width: "80%",
      }}
    >
      <Box>
        <center>
          <Box
            component={"img"}
            src={Logo}
            sx={{
              width: "100px",
              height: "100px",
              objectFit: "contain",
              "&:hover": {
                cursor: "pointer",
              },
            }}
            onClick={() => {
              navigate("/");
            }}
          />
          <Typography style={commonStyle.titleText} mb={1}>
            Get Started Now
          </Typography>
        </center>

        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            password: "",
          }}
          validate={validate}
          onSubmit={(values, { resetForm }) => {
            handleRegister(values, resetForm);
          }}
        >
          {({ handleSubmit }) => (
            <Form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column" }}
            >
              <Typography sx={commonStyle.inputLabelStyle}>
                First Name
              </Typography>
              <Field name="firstName">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    type="text"
                    placeholder="Enter your first name"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    slotProps={commonStyle.inputSlotTextStyle}
                    error={meta.touched && !!meta.error}
                    helperText={
                      meta.touched && meta.error ? meta.error._errors?.[0] : ""
                    }
                  />
                )}
              </Field>

              <Typography sx={commonStyle.inputLabelStyle}>
                Last Name
              </Typography>
              <Field name="lastName">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    type="text"
                    placeholder="Enter your last name"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    slotProps={commonStyle.inputSlotTextStyle}
                    error={meta.touched && !!meta.error}
                    helperText={
                      meta.touched && meta.error ? meta.error._errors?.[0] : ""
                    }
                  />
                )}
              </Field>

              <Typography sx={commonStyle.inputLabelStyle}>
                Email address
              </Typography>
              <Field name="email">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    type="email"
                    placeholder="Enter your email address"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    fullWidth
                    slotProps={commonStyle.inputSlotTextStyle}
                    error={meta.touched && !!meta.error}
                    helperText={
                      meta.touched && meta.error ? meta.error._errors?.[0] : ""
                    }
                  />
                )}
              </Field>

              <Typography sx={commonStyle.inputLabelStyle}>Password</Typography>
              <Field name="password">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    type={isPasswordVisible ? "text" : "password"}
                    margin="dense"
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter Password"
                    slotProps={{
                      input: {
                        sx: {
                          fontSize: "0.875rem",
                          color: theme.palette.text.primary,
                          fontWeight: 400,
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                              aria-label="toggle password visibility"
                            >
                              {isPasswordVisible ? (
                                <EyeHiddenIcon />
                              ) : (
                                <EyeVisibleIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    slots={{ input: OutlinedInput }}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={
                      meta.touched && meta.error ? meta.error._errors?.[0] : ""
                    }
                  />
                )}
              </Field>

              <Box sx={commonStyle.leftAlignText} mt={1}>
                <Checkbox
                  size="small"
                  value={isChecked}
                  onChange={handleCheckboxChange}
                />
                <Typography sx={styles.agreementText}>
                  I agree to the{" "}
                  <Link
                    to={"/terms-condition"}
                    style={styles.termsConditionText}
                  >
                    terms & condition
                  </Link>
                </Typography>
              </Box>

              {loading ? (
                <CircularProgress sx={{ mt: 4 }} />
              ) : (
                <PrimaryButton
                  title="Sign Up"
                  onClick={handleSubmit}
                  viewStyle={{ width: "100%", mt: 4 }}
                />
              )}
            </Form>
          )}
        </Formik>

        <Divider
          flexItem
          sx={{ borderColor: "rgba(245, 245, 245, 1)", mt: 4, pb: 4 }}
        >
          Or
        </Divider>
        <center>
          <Typography sx={styles.doNotHaveAccount}>
            Have an account?{" "}
            <Link to={"/auth/login"} style={styles.signupBtn}>
              Sign In
            </Link>
          </Typography>
        </center>
      </Box>
    </motion.div>
  );
};
export default SignupPage;
const styles = {
  forgetPasswordText: {
    fontSize: "clamp(0.6rem, 4vw, 0.825rem)",
    color: theme.palette.primary.main,
    "&:hover": {
      cursor: "pointer",
    },
  },
  agreementText: {
    fontSize: "clamp(0.6rem, 4vw, 0.825rem)",
    color: theme.palette.text.primary,
  },
  termsConditionText: {
    color: theme.palette.primary.main,
  },
  doNotHaveAccount: {
    fontSize: "clamp(0.85rem, 4vw, 1rem)",
    color: theme.palette.text.primary,
  },
  signupBtn: {
    color: theme.palette.primary.main,
  },
};
